import { useEffect, useRef } from 'react';
import { useAnalytics } from './useAnalytics';
import { AnalyticsEvent } from '../constants/analyticsEvents';
import { useShallow } from 'zustand/react/shallow';
import { useStoreInfoStore } from '../../stores/useStoreInfoStore';
import { sendConversionEvent } from '../services/chat-api.service';

interface UseConversionTrackingOptions {
  urls?: string[];
  superbarVisible?: boolean;
  isLoadingFlags?: boolean;
}

/**
 * Fires CONVERSION_PAGE_DISPLAYED once per page load if the current URL
 * matches any of the configured conversion URLs (pathname startsWith match).
 * Includes superbarConversion: true if the session has any non-onboarding messages.
 */
export const useConversionTracking = ({
  urls,
  superbarVisible,
  isLoadingFlags,
}: UseConversionTrackingOptions): void => {
  const { trackEvent } = useAnalytics();
  // useShallow prevents re-renders when store/currentSession refs are unchanged;
  // without it, the selector returns a new object each time and the hook would re-run the effect on every store update.
  const { store, currentSession } = useStoreInfoStore(
    useShallow((s) => ({ store: s.store, currentSession: s.currentSession }))
  );
  const hasFired = useRef(false);

  useEffect(() => {
    if (
      !urls?.length ||
      hasFired.current ||
      !store ||
      !currentSession ||
      isLoadingFlags
    )
      return;

    const pathname = window.location.pathname;
    const matchedUrl = urls.find((url) => pathname.startsWith(url));
    if (!matchedUrl) return;

    const trackConversion = async () => {
      try {
        trackEvent(AnalyticsEvent.CONVERSION_PAGE_DISPLAYED, {
          url: matchedUrl,
          pathname,
          superbarVisible,
        });
        await sendConversionEvent({
          storeId: store.id,
          session: currentSession,
          data: { superbarVisible },
        });
        hasFired.current = true;
      } catch (error) {
        console.error('Conversion tracking failed:', error);
      }
    };

    trackConversion();
  }, [
    urls,
    trackEvent,
    store,
    currentSession,
    superbarVisible,
    isLoadingFlags,
  ]);
};
